import { ADTS, BlobSource, Input, MP3, WEBM } from 'mediabunny';

import { CorrelatedLogger } from '@eluve/logger';

/**
 * Get the duration of an audio file using MediaBunny for accurate parsing.
 * This properly handles WebM files with chunked recording by reading the
 * actual duration from the container metadata.
 *
 * @param file - The audio file to analyze
 * @returns Duration in milliseconds
 */
export const getAudioDurationFromFile = async (file: File): Promise<number> => {
  const input = new Input({
    source: new BlobSource(file),
    formats: [WEBM, MP3, ADTS],
  });

  // computeDuration() returns seconds, convert to milliseconds
  const durationSeconds = await input.computeDuration();
  return Math.round(durationSeconds * 1000);
};

/**
 * Safely get audio duration from file with error handling and logging.
 * MediaBunny accurately reads the duration from the WebM container metadata,
 * so each chunk file reports its own actual duration correctly.
 *
 * @param file - The audio file to analyze
 * @param log - Logger instance for diagnostics
 * @returns Duration in milliseconds, or null if calculation fails
 */
export const safeGetAudioDurationFromFile = async ({
  file,
  log,
}: {
  file: File;
  log: CorrelatedLogger;
}): Promise<number | null> => {
  try {
    const startTime = performance.now();
    const recordingDuration = await getAudioDurationFromFile(file);

    log('info', 'Calculating audio duration from file completed', {
      recordingDuration,
      fileName: file.name,
      timeToCalculate: performance.now() - startTime,
    });

    return recordingDuration;
  } catch (error) {
    log('warn', 'Failed to get audio duration from file', {
      error,
      fileName: file.name,
    });
    return null;
  }
};
